import type { ComponentType } from "react";
import {
  ShieldCheck, Lock, RefreshCw, Fingerprint, Webhook, FileText,
  Send, Code2, KeyRound, Building2, CheckCircle2,
} from "lucide-react";
import { API_URL } from "@/lib/api";

export const FEATURES: { icon: ComponentType<{ className?: string }>; title: string; desc: string; tag: string }[] = [
  {
    icon: ShieldCheck,
    title: "Certificado DGII",
    desc: "Soporte completo E31–E34, E41, E43–E47. Aprobado por la DGII para producción en todos los ambientes.",
    tag: "E31–E47",
  },
  {
    icon: Lock,
    title: "Firma con P12 Cifrado",
    desc: "Carga tu certificado P12 con contraseña cifrada AES-256. Firma digital automática en cada comprobante emitido.",
    tag: "AES-256",
  },
  {
    icon: RefreshCw,
    title: "RFCE Automático",
    desc: "Representación Fiscal de Consumidores Electrónica generada automáticamente para E32 menores de RD$250,000.",
    tag: "E32 <250k",
  },
  {
    icon: Fingerprint,
    title: "Idempotencia Total",
    desc: "Reintentos seguros con idempotency_key. Nunca se duplican NCF aunque el ERP reintente la misma solicitud.",
    tag: "Sin duplicados",
  },
  {
    icon: Webhook,
    title: "Webhooks en Tiempo Real",
    desc: "Notificaciones instantáneas de estado DGII vía webhook HTTP. Consulta de estado por TrackId también disponible.",
    tag: "Real-time",
  },
  {
    icon: FileText,
    title: "Auditoría Completa",
    desc: "XML firmado archivado, TrackId, código QR de verificación y Representación Impresa (RI) en PDF descargable.",
    tag: "XML + QR + PDF",
  },
];

export const COMPROBANTES = [
  { code: "E31", name: "Crédito Fiscal", status: "ValidadoDGII" },
  { code: "E32", name: "Consumo (ECF y RFCE)", status: "ValidadoDGII" },
  { code: "E33", name: "Nota de Débito", status: "ValidadoDGII" },
  { code: "E34", name: "Nota de Crédito", status: "ValidadoDGII" },
  { code: "E41", name: "Compras", status: "ValidadoDGII" },
  { code: "E43", name: "Gastos Menores", status: "ValidadoDGII" },
  { code: "E44", name: "Régimen Especial", status: "ValidadoDGII" },
  { code: "E45", name: "Gubernamental", status: "ValidadoDGII" },
  { code: "E46", name: "Exportaciones", status: "ValidadoDGII" },
  { code: "E47", name: "Pagos al Exterior", status: "ValidadoDGII" },
];

export const STEPS: { num: string; icon: ComponentType<{ className?: string }>; title: string; desc: string }[] = [
  {
    num: "01",
    icon: Send,
    title: "ERP envía JSON",
    desc: "Tu ERP (Odoo, SAP, Dynamics) llama POST /api/v1/ecf con el payload del comprobante.",
  },
  {
    num: "02",
    icon: Code2,
    title: "Generamos XML e-CF",
    desc: "Validamos y transformamos el JSON a XML firmado según el XSD oficial de la DGII.",
  },
  {
    num: "03",
    icon: KeyRound,
    title: "Firma Digital",
    desc: "Firmamos el XML con tu certificado P12 cargado de forma segura en el vault cifrado.",
  },
  {
    num: "04",
    icon: Building2,
    title: "Envío DGII",
    desc: "Enviamos al ambiente correcto (TesteCF/CerteCF/eCF) y consultamos el estado.",
  },
  {
    num: "05",
    icon: CheckCircle2,
    title: "TrackId y RI",
    desc: "Devolvemos XML, TrackId, código de seguridad, QR y RI PDF. Estado: AceptadoDGII.",
  },
];

export const PRICING = [
  {
    name: "Esencial",
    price: "75",
    currency: "USD",
    period: "/mes",
    desc: "Para el negocio que ya factura pero lleva el inventario en Excel.",
    popular: false,
    features: [
      "Hasta 250 e-CF mensuales",
      "2 usuarios incluidos (adicional: $12/mes)",
      "Odoo ERP preconfigurado (Facturación, Ventas, POS, Inventario y Compras)",
      "Firma con tu P12, envío y validación ante la DGII",
      "RFCE para consumo electrónico (E32)",
      "NC y ND (E33/E34) cuentan como e-CF emitido",
      "Reportes DGII 606, 607 y 608 automáticos",
      "Base de datos dedicada y respaldo diario",
      "Soporte técnico y fiscal + office hours grupales",
      "Implementación: USD$ 350 (50% desc. con pago anual)",
    ],
    cta: "Comenzar Esencial",
  },
  {
    name: "Crecimiento",
    price: "130",
    currency: "USD",
    period: "/mes",
    desc: "Para el negocio con más de un punto de venta o más de una sucursal.",
    popular: true,
    features: [
      "Hasta 750 e-CF mensuales",
      "4 usuarios incluidos (adicional: $12/mes)",
      "Todo lo de Esencial + multi-sucursal y multi-caja",
      "Control de turnos y arqueos de caja",
      "Monitoreo activo de rechazos DGII con alertas",
      "Reportes 606, 607, 608 + conciliación fiscal",
      "Base de datos dedicada y respaldo diario",
      "Soporte técnico y fiscal + office hours grupales",
      "Implementación: USD$ 350 (GRATIS con pago anual)",
    ],
    cta: "Comenzar Crecimiento",
  },
  {
    name: "Avanzado",
    price: "200",
    currency: "USD",
    period: "/mes",
    desc: "Para la empresa con operación de almacén real o varias razones sociales.",
    popular: false,
    features: [
      "Hasta 2,000 e-CF mensuales",
      "8 usuarios incluidos (adicional: $12/mes)",
      "Todo lo de Crecimiento + múltiples almacenes",
      "Reglas de reabastecimiento e inventario",
      "Multi-compañía y acceso a la API",
      "Emisión en contingencia asistida",
      "Reportes contables e inventarios automatizados",
      "Soporte prioritario (respuesta en 4h hábiles)",
      "Implementación: USD$ 350 (GRATIS con pago anual)",
    ],
    cta: "Comenzar Avanzado",
  },
];

export const FAQS = [
  {
    q: "¿Qué incluye el soporte técnico de mi plan?",
    a: "Soporte para la emisión de e-CF, resolución de incidencias con la DGII, firma con tu certificado P12, monitoreo de la plataforma y respaldo diario de tu información. Además, todos los clientes tienen acceso a nuestras sesiones grupales semanales de preguntas y respuestas, donde puedes plantear cualquier duda de operación.",
    cat: "Soporte",
  },
  {
    q: "¿El plan incluye capacitación o configuración contable de Odoo?",
    a: "La plataforma se entrega preconfigurada, lista para operar y con guías de uso, e incluye dos sesiones de entrenamiento durante la implementación. Después de eso, el soporte de la suscripción cubre incidencias técnicas y fiscales; la operación interna del negocio es autogestionada, con el apoyo de las sesiones grupales semanales y nuestra base de conocimiento. Para capacitaciones adicionales o configuraciones específicas contamos con el Pack de Acompañamiento PyME.",
    cat: "Odoo",
  },
  {
    q: "¿Qué pasa si supero mi cantidad de comprobantes?",
    a: "Nada se detiene. Sigues emitiendo con normalidad y facturamos los bloques adicionales en tu siguiente ciclo, a USD$ 12 por cada 100 comprobantes. Solo contamos los comprobantes aceptados por la DGII, de cualquier tipo emitido (incluye NC/ND). Los rechazos, reintentos, anulaciones y los e-CF recibidos no consumen tu cuota.",
    cat: "Comprobantes",
  },
  {
    q: "¿Qué ocurre si el sistema falla y no puedo facturar?",
    a: "Ante una caída del servicio, un error de base de datos o una falla de comunicación con la DGII, nuestro equipo interviene de forma prioritaria y sin costo adicional. Adicionalmente, tu instancia cuenta con el procedimiento de emisión en contingencia previsto en la normativa, para que puedas seguir vendiendo mientras se restablece el servicio.",
    cat: "Contingencia",
  },
  {
    q: "¿Necesito comprar algo más además del plan?",
    a: "Sí: el certificado digital de firma para procedimientos tributarios, que debe estar a nombre de tu empresa o de tu representante autorizado y se adquiere con una entidad certificadora autorizada por INDOTEL. Si vas a usar punto de venta, también necesitarás impresora y lector de código de barras. Te orientamos en ambas compras sin costo.",
    cat: "Requisitos",
  },
  {
    q: "¿Qué pasa con mi información si cancelo?",
    a: "Te entregamos un respaldo completo y descargable de tu base de datos dentro de los 30 días posteriores a la cancelación. Tu información es tuya.",
    cat: "Datos",
  },
];

const API_BASE = API_URL.replace(/\/$/, "");

export const CODE_SAMPLES = {
  curl: `curl -X POST ${API_BASE}/ecf \\
  -H "X-API-Key: sk_live_xxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "idempotency_key": "E31-INV-2026-0001",
    "external_id": "INV/2026/0001",
    "ecf_type": "31",
    "environment": "TesteCF",
    "payload": {
      "Encabezado": {
        "Version": "1.0",
        "IdDoc": {
          "TipoeCF": "31",
          "eNCF": "E310000000001",
          "FechaVencimientoSecuencia": "31-12-2028",
          "IndicadorMontoGravado": 0,
          "TipoIngresos": "01",
          "TipoPago": "1"
        },
        "Emisor": {
          "RNCEmisor": "130478031",
          "RazonSocialEmisor": "EMPRESA DEMO SRL",
          "NombreComercial": "EMPRESA DEMO",
          "DireccionEmisor": "AV. DEMO 1",
          "FechaEmision": "28-04-2026"
        },
        "Comprador": {
          "RNCComprador": "131098193",
          "RazonSocialComprador": "CLIENTE DEMO SRL"
        },
        "Totales": {
          "MontoGravadoTotal": 10000.00,
          "MontoGravadoI1": 10000.00,
          "MontoExento": 0.00,
          "ITBIS1": 18,
          "TotalITBIS": 1800.00,
          "TotalITBIS1": 1800.00,
          "MontoTotal": 11800.00,
          "TotalITBISRetenido": 0.00
        }
      },
      "DetallesItems": {
        "Item": [
          {
            "NumeroLinea": 1,
            "IndicadorFacturacion": 1,
            "NombreItem": "SERVICIO DEMO",
            "IndicadorBienoServicio": 2,
            "CantidadItem": 1.00,
            "UnidadMedida": "43",
            "PrecioUnitarioItem": 10000.00,
            "MontoItem": 10000.00
          }
        ]
      }
    }
  }'`,
  python: `from ecfservice import ECFClient

# Utilizando el SDK oficial de Python (pip install ecfservice)
with ECFClient(api_key="sk_live_xxxxxxxxxxxxxxxx") as client:
    doc = client.ecf.create(
        idempotency_key="E31-INV-2026-0001",
        ecf_type="31",
        payload={
            "Encabezado": {
                "Version": "1.0",
                "IdDoc": {
                    "TipoeCF": "31",
                    "eNCF": "E310000000001",
                    "FechaVencimientoSecuencia": "31-12-2028",
                    "IndicadorMontoGravado": 0,
                    "TipoIngresos": "01",
                    "TipoPago": "1"
                },
                "Emisor": {
                    "RNCEmisor": "130478031",
                    "RazonSocialEmisor": "EMPRESA DEMO SRL",
                    "FechaEmision": "28-04-2026"
                },
                "Comprador": {
                    "RNCComprador": "131098193",
                    "RazonSocialComprador": "CLIENTE DEMO SRL"
                },
                "Totales": {
                    "MontoTotal": 11800.00,
                    "TotalITBIS": 1800.00
                }
            },
            "DetallesItems": {
                "Item": [{
                    "NumeroLinea": 1,
                    "NombreItem": "SERVICIO DEMO",
                    "MontoItem": 10000.00
                }]
            }
        }
    )
    print(f"TrackId: {doc.track_id} - Estado: {doc.status}")`,
  node: `const response = await fetch(
  "${API_BASE}/ecf",
  {
    method: "POST",
    headers: {
      "X-API-Key": "sk_live_xxxxxxxxxxxxxxxx",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      idempotency_key: "E31-INV-2026-0001",
      external_id: "INV/2026/0001",
      ecf_type: "31",
      environment: "TesteCF",
      payload: {
        Encabezado: {
          Version: "1.0",
          IdDoc: {
            TipoeCF: "31",
            eNCF: "E310000000001",
            FechaVencimientoSecuencia: "31-12-2028",
            IndicadorMontoGravado: 0,
            TipoIngresos: "01",
            TipoPago: "1",
          },
          Emisor: {
            RNCEmisor: "130478031",
            RazonSocialEmisor: "EMPRESA DEMO SRL",
            FechaEmision: "28-04-2026",
          },
          Comprador: {
            RNCComprador: "131098193",
            RazonSocialComprador: "CLIENTE DEMO SRL",
          },
          Totales: {
            MontoGravadoTotal: 10000.00,
            TotalITBIS: 1800.00,
            MontoTotal: 11800.00,
          },
        },
        DetallesItems: {
          Item: [
            {
              NumeroLinea: 1,
              NombreItem: "SERVICIO DEMO",
              MontoItem: 10000.00,
            },
          ],
        },
      },
    }),
  }
);

const data = await response.json();
console.log('TrackId: ' + data.track_id);`,
  csharp: `using EcfService.Sdk;
using EcfService.Sdk.Models;

// Utilizando el SDK oficial de C# / .NET (NuGet EcfService.Sdk)
using var client = new EcfClient("sk_live_xxxxxxxxxxxxxxxx");

var payload = new Dictionary<string, object>
{
    { "Encabezado", new Dictionary<string, object>
        {
            { "Version", "1.0" },
            { "IdDoc", new Dictionary<string, object> { 
                { "TipoeCF", "31" },
                { "eNCF", "E310000000001" },
                { "FechaVencimientoSecuencia", "31-12-2028" },
                { "IndicadorMontoGravado", 0 },
                { "TipoIngresos", "01" },
                { "TipoPago", "1" }
            }},
            { "Emisor", new Dictionary<string, object> { 
                { "RNCEmisor", "130478031" },
                { "RazonSocialEmisor", "EMPRESA DEMO SRL" },
                { "FechaEmision", "28-04-2026" }
            }},
            { "Comprador", new Dictionary<string, object> { 
                { "RNCComprador", "131098193" },
                { "RazonSocialComprador", "CLIENTE DEMO SRL" }
            }},
            { "Totales", new Dictionary<string, object> { 
                { "MontoTotal", 11800.00 },
                { "TotalITBIS", 1800.00 }
            }}
        }
    },
    { "DetallesItems", new Dictionary<string, object>
        {
            { "Item", new List<object> {
                new Dictionary<string, object> {
                    { "NumeroLinea", 1 },
                    { "NombreItem", "SERVICIO DEMO" },
                    { "MontoItem", 10000.00 }
                }
            }}
        }
    }
};

EcfDocument doc = await client.Ecf.CreateAsync(
    idempotencyKey: "E31-INV-2026-0001",
    ecfType: EcfType.E31,
    payload: payload
);

Console.WriteLine($"TrackId: {doc.TrackId} — Estado: {doc.Status}");`,
};

export const METRICS = [
  { value: 300000, label: "Transacciones / día", suffix: "+" },
  { value: 99.95, label: "SLA garantizado", suffix: "%", decimal: true },
  { value: 2, label: "Latencia promedio", suffix: "s <" },
  { value: 100, label: "Trazabilidad", suffix: "%" },
];

export const STATUS_COMPONENTS = [
  { name: "API Gateway", status: "operational", uptime: "99.98%", lastIncident: "Hace 43 días" },
  { name: "Firma Digital", status: "operational", uptime: "99.99%", lastIncident: "Hace 71 días" },
  { name: "Envío DGII", status: "operational", uptime: "99.95%", lastIncident: "Hace 12 días" },
  { name: "Consulta Estado", status: "operational", uptime: "99.97%", lastIncident: "Hace 28 días" },
  { name: "Webhooks", status: "operational", uptime: "99.96%", lastIncident: "Hace 19 días" },
  { name: "Portal / Dashboard", status: "operational", uptime: "99.99%", lastIncident: "Hace 61 días" },
];

export const DOC_SECTIONS = [
  {
    group: "Comenzar",
    items: [
      { id: "introduccion", label: "Introducción" },
      { id: "crear-empresa", label: "Crear empresa" },
      { id: "api-key", label: "Obtener API Key" },
      { id: "cargar-p12", label: "Cargar certificado P12" },
      { id: "rangos-ncf", label: "Cargar rangos NCF" },
    ],
  },
  {
    group: "Conceptos",
    items: [
      { id: "ambientes", label: "Ambientes TesteCF/CerteCF/eCF" },
      { id: "tipos-ecf", label: "Tipos de e-CF" },
      { id: "idempotencia", label: "Idempotencia" },
      { id: "estados-fsm", label: "Estados FSM" },
    ],
  },
  {
    group: "API Reference",
    items: [
      { id: "post-ecf", label: "POST /api/v1/ecf" },
      { id: "get-ecf", label: "GET /api/v1/ecf/{id}" },
      { id: "get-xml", label: "GET /api/v1/ecf/{id}/xml" },
      { id: "get-ri", label: "GET /api/v1/ecf/{id}/ri" },
      { id: "summary", label: "GET /api/v1/client/summary" },
      { id: "webhooks", label: "Webhooks" },
    ],
  },
  {
    group: "Guías",
    items: [
      { id: "guia-odoo", label: "Integración Odoo" },
      { id: "guia-erp", label: "Integración ERPs genéricos" },
      { id: "errores-dgii", label: "Manejo de errores DGII" },
      { id: "certificacion", label: "Certificación DGII paso a paso" },
    ],
  },
  {
    group: "Recursos",
    items: [
      { id: "codigos-error", label: "Códigos de error" },
      { id: "glosario", label: "Glosario fiscal" },
      { id: "changelog", label: "Changelog" },
      { id: "estado-api", label: "Estado del servicio" },
    ],
  },
];
